import { Box, Button, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "components";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import { FormField } from "empower-energy-owner-components";
import { BATTERY_OEMS, READWRITE } from "constants/select";
import { getOptions } from "actions/option";
import {
  createVppUserRegistration,
  getVppUserRegistration,
  searchLocation,
  updateVppUserRegistration,
} from "actions/vppUserRegistration";
import useDebounce from "hooks/useDebounce";

// Minimum characters before hitting the location_search endpoint.
const MIN_SEARCH_LENGTH = 2;

const emptyForm = {
  email: "",
  vpp: "",
  oem: "",
  external_reference: "",
  nmi_provided: "",
  city: "",
  state_code: "",
  postcode: "",
};

const locationValue = (data) =>
  data?.city
    ? {
        city: data.city,
        state_code: data.state_code,
        postcode: data.postcode,
        label: `${data.city} ${data.state_code}`,
      }
    : null;

const AddEditVppRegistrationModal = ({ selectedRegistration, open, onClose }) => {
  const dispatch = useDispatch();

  const [isEdit, setEdit] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({ email: "", vpp: "" });
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationInput, setLocationInput] = useState("");

  const loading = useSelector((state) => state.vppUserRegistration?.loading);
  const detail = useSelector((state) => state.vppUserRegistration?.detail);
  const locationOptions = useSelector(
    (state) => state.vppUserRegistration?.locationOptions || [],
  );
  const locationLoading = useSelector(
    (state) => state.vppUserRegistration?.locationLoading,
  );
  const vppOptions = useSelector((state) => state.option?.vpp || []);

  const debouncedLocation = useDebounce(locationInput, 300);

  useEffect(() => {
    if (vppOptions.length === 0) {
      dispatch(getOptions());
    }
  }, [dispatch]);

  useEffect(() => {
    if (debouncedLocation && debouncedLocation.length >= MIN_SEARCH_LENGTH) {
      dispatch(searchLocation(debouncedLocation));
    }
  }, [dispatch, debouncedLocation]);

  useEffect(() => {
    if (!isEmpty(selectedRegistration)) {
      setEdit(true);
      // Seed from the list row, then fetch the detail for the full record.
      setFormData({ ...emptyForm, ...selectedRegistration });
      setSelectedLocation(locationValue(selectedRegistration));
      dispatch(getVppUserRegistration(selectedRegistration.id));
    } else {
      setEdit(false);
      setFormData(emptyForm);
      setSelectedLocation(null);
    }
  }, [selectedRegistration]);

  useEffect(() => {
    if (isEdit && detail?.id === selectedRegistration?.id) {
      setFormData((prev) => ({ ...prev, ...detail }));
      setSelectedLocation(locationValue(detail));
    }
  }, [detail]);

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    checkErrors(e);
  };

  const checkErrors = (e) => {
    if (!(e.target.name in errors)) return;
    setErrors({
      ...errors,
      [e.target.name]: isEmpty(String(e.target.value)) ? "Required" : "",
    });
  };

  const onSelectLocation = (event, value) => {
    setSelectedLocation(value);
    setFormData((prev) => ({
      ...prev,
      city: value?.city || "",
      state_code: value?.state_code || "",
      postcode: value?.postcode || "",
    }));
  };

  const onClickSave = (e) => {
    e.preventDefault();

    const location = {
      city: formData.city || undefined,
      state_code: formData.state_code || undefined,
      postcode: formData.postcode ? Number(formData.postcode) : undefined,
    };

    if (isEdit) {
      // Edit is narrow: email, vpp and access_type are not editable.
      const body = {
        oem: formData.oem || undefined,
        external_reference: formData.external_reference || undefined,
        nmi_provided: formData.nmi_provided || undefined,
        ...location,
      };
      dispatch(
        updateVppUserRegistration(
          selectedRegistration.owner,
          selectedRegistration.id,
          body,
          () => {
            onClose();
          },
        ),
      );
    } else {
      const body = {
        email: formData.email,
        vpp: formData.vpp,
        access_type: READWRITE,
        oem: formData.oem || undefined,
        external_reference: formData.external_reference || undefined,
        nmi_provided: formData.nmi_provided || undefined,
        ...location,
      };
      dispatch(
        createVppUserRegistration(body, () => {
          onClose();
        }),
      );
    }
  };

  const anyError = () => !Object.values(errors).every((it) => isEmpty(it));
  const invalid = anyError() || isEmpty(formData.email) || isEmpty(formData.vpp);

  return (
    <Box>
      <Modal
        title={isEdit ? "Edit Registration" : "Add New VPP User Registration"}
        open={open}
        onClose={onClose}
        width={500}
        top={"10%"}
      >
        <form>
          {isEdit ? (
            <Box mb={2}>
              <Typography variant="body1" fontWeight={500}>
                {formData.email}
              </Typography>
              <Typography variant="body2" mt={1}>
                {vppOptions.find((it) => it.value === formData.vpp)?.label ||
                  formData.vpp}
              </Typography>
            </Box>
          ) : (
            <>
              <FormField
                label="User Email"
                placeholder="User Email"
                name="email"
                variant="standard"
                type="email"
                value={formData.email}
                onChange={onChange}
                error={errors.email}
                onBlur={checkErrors}
              />
              <FormField
                label="VPP"
                name="vpp"
                type="select"
                variant="standard"
                value={formData.vpp}
                onChange={onChange}
                options={vppOptions}
                error={errors.vpp}
              />
            </>
          )}

          <FormField
            label="OEM"
            name="oem"
            type="select"
            variant="standard"
            value={formData.oem}
            onChange={onChange}
            options={BATTERY_OEMS}
          />
          <FormField
            label="External Reference (Station ID)"
            placeholder="External Reference (Station ID)"
            name="external_reference"
            variant="standard"
            value={formData.external_reference}
            onChange={onChange}
          />
          <FormField
            label="National Meter Identifier (NMI)"
            placeholder="National Meter Identifier (NMI)"
            name="nmi_provided"
            variant="standard"
            value={formData.nmi_provided}
            onChange={onChange}
          />

          <Box mt={2}>
            <FormField
              type="autocomplete"
              label="Search suburb or postcode"
              variant="standard"
              options={locationOptions}
              value={selectedLocation}
              loading={locationLoading}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.city === value.city &&
                option.state_code === value.state_code &&
                option.postcode === value.postcode
              }
              onChange={onSelectLocation}
              onInputChange={(event, value, reason) => {
                if (reason === "input") setLocationInput(value);
              }}
              noOptionsText={
                locationInput.trim().length < MIN_SEARCH_LENGTH
                  ? `Type at least ${MIN_SEARCH_LENGTH} characters`
                  : "No matching locations"
              }
            />
          </Box>

          <Box
            className="items-center space-between"
            mt={4}
            sx={{
              "& button": {
                width: "48%",
              },
            }}
          >
            <LoadingButton
              loading={loading}
              onClick={onClickSave}
              variant="contained"
              disabled={invalid}
            >
              Confirm
            </LoadingButton>
            <Button onClick={onClose} variant="outlined">
              Cancel
            </Button>
          </Box>
        </form>
      </Modal>
    </Box>
  );
};

export default AddEditVppRegistrationModal;
